import React from 'react';
import LegalLayout, { H2, H3, P, UL } from '@/components/LegalLayout';

export default function TermsOfServicePage() {
  return (
    <LegalLayout eyebrow="LEGAL" title="Terms of Service" updated="June 2026">
      <P>
        Welcome to Christovibes Radio. By listening to our stream, using our website or mobile app,
        submitting music, requesting a song, or posting a prayer request, you agree to these Terms
        of Service. Please read them carefully. If you do not agree, please do not use the service.
      </P>

      <H2>1. Who We Are</H2>
      <P>
        Christovibes Radio ("Christovibes", "we", "our" or "us") is an internet radio station
        broadcasting contemporary African Christian music (gospel, worship, praise and related
        genres) twenty-four hours a day, together with scheduled shows and on-demand catch-up
        episodes.
      </P>

      <H2>2. Your Use of the Service</H2>
      <UL>
        <li>You must be at least 13 years old (or the minimum age in your jurisdiction).</li>
        <li>You will not use Christovibes to break the law, to defame others, to spread hate, to publish sexually explicit content, or to interfere with our stream or systems.</li>
        <li>You will not attempt to reverse-engineer, scrape, or resell the service.</li>
        <li>You will use the service in a spirit consistent with Christian charity — respectful, honest, and kind.</li>
      </UL>

      <H2>3. Music Submissions</H2>
      <H3>What you promise us</H3>
      <UL>
        <li>You confirm that you own — or have secured written permission for — all rights (recording, songwriting, publishing and any samples) in the audio you upload.</li>
        <li>You grant Christovibes a non-exclusive, worldwide, royalty-free licence to broadcast, stream, catalogue, promote and archive the track on Christovibes Radio and its official platforms.</li>
        <li>You agree that Christovibes may reject any submission at its sole discretion, without owing an explanation.</li>
      </UL>
      <H3>What we promise you</H3>
      <UL>
        <li>We will not resell your track or use it outside our broadcast without your consent.</li>
        <li>If a track is approved and broadcast, we will credit you as the artist.</li>
        <li>You can request removal at any time by writing to <a className="text-[#D4AF37] hover:underline" href="mailto:music@christovibes.com">music@christovibes.com</a>. We will remove it from active rotation within a reasonable period, subject to broadcast logs already archived.</li>
      </UL>

      <H2>4. Requests, Dedications & Prayer Wall</H2>
      <UL>
        <li>Content you post publicly (song requests, dedications, prayer wall messages) may be visible to other listeners and displayed on our stream, website and social channels.</li>
        <li>Do not post other people's personal information, contact details, or content that would embarrass or harm them.</li>
        <li>We may edit, hide, or remove any post at our discretion, including for length, clarity, safety or appropriateness.</li>
        <li>The prayer wall is a space of care. Please refrain from theological debate, doctrinal attacks, spam, promotions or self-harm content. If you or someone you know is in immediate danger, contact local emergency services first.</li>
      </UL>

      <H2>5. Copyright & DMCA</H2>
      <P>
        Christovibes respects intellectual property rights. If you believe content on the service
        infringes your copyright, please send a written notice to <a className="text-[#D4AF37] hover:underline" href="mailto:copyright@christovibes.com">copyright@christovibes.com</a> including:
      </P>
      <UL>
        <li>Identification of the work you believe is infringed.</li>
        <li>The exact URL or track title on Christovibes.</li>
        <li>Your contact details and a good-faith statement.</li>
        <li>Your signature (typed name is acceptable).</li>
      </UL>

      <H2>6. Our Content</H2>
      <P>
        The Christovibes name, logo, artwork, station imaging, produced shows, and website are
        owned by Christovibes and protected by copyright and trademark law. You may not copy or
        redistribute them without permission.
      </P>

      <H2>7. Service Availability</H2>
      <P>
        We aim for a reliable 24/7 broadcast, but Christovibes is provided "as is" without any
        warranty of uninterrupted service. Streaming may occasionally be affected by network,
        licensing, or third-party outages beyond our control.
      </P>

      <H2>8. Third-Party Services</H2>
      <P>
        Our service uses trusted third parties (streaming, email delivery, object storage, and AI
        image generation for show art). Their own terms may also apply where relevant. See our
        <a className="text-[#D4AF37] hover:underline" href="/privacy"> Privacy Policy</a> for details.
      </P>

      <H2>9. Limitation of Liability</H2>
      <P>
        To the fullest extent permitted by law, Christovibes shall not be liable for indirect,
        incidental, consequential, or punitive damages arising from your use of the service. Our
        total liability, if any, shall not exceed the amount you paid us in the last twelve months
        (or, for free services, USD 100).
      </P>

      <H2>10. Termination</H2>
      <P>
        We may suspend or terminate access to any user who violates these Terms or acts in ways
        that harm our community, artists, or listeners. You may stop using Christovibes at any time.
      </P>

      <H2>11. Governing Law</H2>
      <P>
        These Terms are governed by the laws of Nigeria (West Africa Time). Disputes will be
        settled by the competent courts of Lagos, Nigeria, unless a mandatory local law provides
        otherwise for you.
      </P>

      <H2>12. Changes</H2>
      <P>
        We may update these Terms occasionally. When we do, we will update the "Last updated" date
        above. Continued use of Christovibes indicates acceptance of the updated Terms.
      </P>

      <H2>13. Contact</H2>
      <P>
        Questions? Write to <a className="text-[#D4AF37] hover:underline" href="mailto:hello@christovibes.com">hello@christovibes.com</a>.
      </P>
    </LegalLayout>
  );
}
